import { useEffect, useMemo, useState } from "react";
import { IdentitySinglePicker, type IdentitySinglePickerConfig } from "./identitySinglePicker.tsx";
import { listResellers, type Reseller } from "../../api/statementConfig.ts";

interface ResellerItem {
  id: number;
  name: string;
}

interface ResellerSinglePickerProps {
  value: number;
  onChange: (id: number) => void;
  disabled?: boolean;
  placeholder?: string;
  onIdEnter?: () => void;
}

/**
 * Form field: pick a single reseller by ID or by typing name.
 *
 * Unlike `OperatorSinglePicker` (which reads from `cache.CommonData`), resellers
 * come from the API. We fetch the active list once on mount via
 * `listResellers(false)` and feed it into the shared `IdentitySinglePicker`.
 */
export function ResellerSinglePicker({ value, onChange, disabled, placeholder, onIdEnter }: ResellerSinglePickerProps) {
  const [resellers, setResellers] = useState<Reseller[]>([]);

  useEffect(() => {
    let live = true;
    listResellers(false)
      .then((r) => { if (live) setResellers(r); })
      .catch(() => { if (live) setResellers([]); });
    return () => { live = false; };
  }, []);

  const items = useMemo<ResellerItem[]>(
    () => resellers.map((r) => ({ id: r.id, name: r.name })),
    [resellers],
  );

  const config = useMemo<IdentitySinglePickerConfig<ResellerItem>>(
    () => ({
      items,
      getId: (r) => r.id,
      nameField: "name",
      noun: "reseller",
      matchFn: (r, q) =>
        String(r.id).includes(q) ||
        r.name.toLowerCase().includes(q),
      secondaryFn: () => "",
    }),
    [items],
  );

  return (
    <IdentitySinglePicker
      value={value}
      onChange={onChange}
      config={config}
      placeholder={placeholder}
      disabled={disabled}
      onIdEnter={onIdEnter}
    />
  );
}
